import { useEffect, useState } from "react";
import { Mail, Phone, Pencil, Save, Star, User, X } from "lucide-react";
import { getFarm, listFarmContacts, updateFarm, type FarmDetail, type FarmContact } from "../../../api/admin";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { TagsManager } from "../TagsManager";
import { COUNTRIES, getProvincesForCountry } from "../../utils/countries";
import { useMemo } from "react";

type ClientOverviewSectionProps = {
  farmId: string;
  onUpdate?: () => void;
};

export function ClientOverviewSection({ farmId, onUpdate }: ClientOverviewSectionProps) {
  const [farm, setFarm] = useState<FarmDetail | null>(null);
  const [contacts, setContacts] = useState<FarmContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "CA",
    lifecycle_status: "lead",
  });

  const availableProvinces = useMemo(() => {
    return getProvincesForCountry(form.country);
  }, [form.country]);

  const provinceOptions = useMemo(() => {
    return availableProvinces.map((p) => `${p.code} - ${p.name}`);
  }, [availableProvinces]);

  const countryOptions = useMemo(() => {
    return COUNTRIES.map((c) => `${c.code} - ${c.name}`);
  }, []);

  const lifecycleOptions = ["lead", "active", "on_hold", "churned"];

  const CONTACT_ROLES = [
    { value: "owner", label: "Propriétaire" },
    { value: "manager", label: "Gestionnaire" },
    { value: "employee", label: "Employé" },
    { value: "agronomist", label: "Agronome" },
    { value: "accounting", label: "Comptabilité" },
    { value: "other", label: "Autre" },
  ];
  const roleLabel = (role: string) => CONTACT_ROLES.find((r) => r.value === role)?.label ?? role;

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getFarm(farmId)
      .then((data) => {
        if (!mounted) return;
        setFarm(data);
        setForm({
          name: data.organization.name || "",
          email: data.organization.email || "",
          phone: data.organization.phone || "",
          address_line1: data.organization.address_line1 || "",
          address_line2: data.organization.address_line2 || "",
          city: data.organization.city || "",
          province: data.organization.province || "",
          postal_code: data.organization.postal_code || "",
          country: data.organization.country || "CA",
          lifecycle_status: data.profile.lifecycle_status || "lead",
        });
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load farm details:", error);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [farmId]);

  useEffect(() => {
    if (!farmId) return;
    let mounted = true;
    setContactsLoading(true);
    listFarmContacts(farmId)
      .then((data) => {
        if (mounted) setContacts(data);
      })
      .catch((e) => {
        if (mounted) setContacts([]);
        console.error("Failed to load contacts:", e);
      })
      .finally(() => {
        if (mounted) setContactsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [farmId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (farm) {
      setForm({
        name: farm.organization.name || "",
        email: farm.organization.email || "",
        phone: farm.organization.phone || "",
        address_line1: farm.organization.address_line1 || "",
        address_line2: farm.organization.address_line2 || "",
        city: farm.organization.city || "",
        province: farm.organization.province || "",
        postal_code: farm.organization.postal_code || "",
        country: farm.organization.country || "CA",
        lifecycle_status: farm.profile.lifecycle_status || "lead",
      });
    }
    setIsEditing(false);
    setErrors({});
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    try {
      const provinceCode = form.province.split(" - ")[0] || form.province;
      const updated = await updateFarm(farmId, {
        organization: {
          name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          address_line1: form.address_line1 || undefined,
          address_line2: form.address_line2 || undefined,
          city: form.city || undefined,
          province: provinceCode || undefined,
          postal_code: form.postal_code || undefined,
          country: form.country || undefined,
        },
        profile: {
          lifecycle_status: form.lifecycle_status,
        },
      });
      setFarm(updated);
      setIsEditing(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Failed to update farm:", error);
      alert("Erreur lors de la mise à jour du client. Veuillez réessayer.");
    }
  };

  const handleCountryChange = (value: string) => {
    const countryCode = value.split(" - ")[0] || value;
    const newProvinces = getProvincesForCountry(countryCode);
    const defaultProvince = newProvinces.length > 0 ? `${newProvinces[0].code} - ${newProvinces[0].name}` : "";
    setForm({
      ...form,
      country: countryCode,
      province: defaultProvince,
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  if (!farm) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Impossible de charger les détails du client</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">Détails du client</h3>
        {!isEditing ? (
          <Button
            label="Modifier"
            intent="default"
            size="sm"
            icon={<Pencil size={14} strokeWidth={1.5} />}
            onClick={handleEdit}
          />
        ) : (
          <div className="flex items-center gap-2">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              icon={<X size={14} strokeWidth={1.5} />}
              onClick={handleCancel}
            />
            <Button
              label="Enregistrer"
              intent="primary"
              size="sm"
              icon={<Save size={14} strokeWidth={1.5} />}
              onClick={handleSave}
            />
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Input
            label="Nom de la ferme"
            placeholder="Ferme des Trois Rivières"
            value={form.name}
            onChange={(event) => {
              setForm({ ...form, name: event.target.value });
              if (errors.name) setErrors({ ...errors, name: "" });
            }}
            disabled={!isEditing}
            state={errors.name ? "error" : "default"}
          />
          {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
        </div>

        <div>
          <Select
            label="Statut lifecycle"
            options={lifecycleOptions}
            value={form.lifecycle_status}
            onChange={(value) => setForm({ ...form, lifecycle_status: value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Email"
            placeholder="email@ferme.com"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Téléphone"
            placeholder="(xxx) xxx-xxxx"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Adresse"
            placeholder="123 rue Principale"
            value={form.address_line1}
            onChange={(event) => setForm({ ...form, address_line1: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Adresse (ligne 2)"
            placeholder="Appartement, suite, etc."
            value={form.address_line2}
            onChange={(event) => setForm({ ...form, address_line2: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Ville"
            placeholder="Ville"
            value={form.city}
            onChange={(event) => setForm({ ...form, city: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Input
            label="Code postal"
            placeholder="A1A 1A1"
            value={form.postal_code}
            onChange={(event) => setForm({ ...form, postal_code: event.target.value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Select
            label="Pays"
            options={countryOptions}
            value={`${form.country} - ${COUNTRIES.find((c) => c.code === form.country)?.name || ""}`}
            onChange={handleCountryChange}
            disabled={!isEditing}
          />
        </div>

        <div>
          <Select
            label="Province/État"
            options={provinceOptions}
            value={form.province}
            onChange={(value) => setForm({ ...form, province: value })}
            disabled={!isEditing}
          />
        </div>
      </div>

      {/* Tags Section */}
      <div className="mt-4 pt-4 border-t border-border">
        <TagsManager
          organizationId={farmId}
          organizationType="client"
          onUpdate={onUpdate}
        />
      </div>

      {/* Contacts: même affichage que l'onglet Contact (sans édition/suppression) */}
      <div className="mt-4 pt-4 border-t border-border space-y-4">
        <h4 className="text-sm font-semibold text-text">Contacts</h4>
        {contactsLoading ? (
          <div className="text-sm text-muted py-4 text-center">Chargement...</div>
        ) : contacts.length === 0 ? (
          <div className="rounded-md border border-border bg-surface p-6 text-center text-sm text-muted">
            Aucun contact.
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map((c) => (
              <div
                key={c.id}
                className="rounded-md border border-border bg-surface p-4 shadow-sm flex flex-wrap items-start gap-4"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="rounded-full bg-surface-muted p-2 shrink-0">
                    <User size={18} className="text-muted" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-text flex items-center gap-1.5 flex-wrap">
                      <span>
                        {c.person.first_name} {c.person.last_name}
                      </span>
                      {c.is_primary && (
                        <Star size={16} className="shrink-0 fill-amber-400 text-amber-400" aria-label="Principal" />
                      )}
                    </p>
                    <p className="text-xs text-muted">{roleLabel(c.contact_role)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  {c.person.email && (
                    <a
                      href={`mailto:${c.person.email}`}
                      className="text-sm text-primary hover:underline flex items-center gap-1.5"
                    >
                      <Mail size={14} />
                      {c.person.email}
                    </a>
                  )}
                  {c.person.phone && (
                    <span className="text-sm text-text flex items-center gap-1.5">
                      <Phone size={14} />
                      {c.person.phone}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
